"use client"

import { useEffect, useState } from "react"
import { getSSOToken } from "./auth"
import * as microsoftTeams from "@microsoft/teams-js"
import { Button, Dropdown, TextArea, Alert } from "@fluentui/react-northstar"

const AuroraSidePanel = () => {
  // State for summarization UI
  const [selectedAgentName, setSelectedAgentName] = useState("Default Summarizer")
  const [selectedAgent, setSelectedAgent] = useState("100")
  const [prompt, setPrompt] = useState("")
  const [meetingId, setMeetingId] = useState(null)
  const [graphToken, setGraphToken] = useState(null)
  const [userInfo, setUserInfo] = useState("Unknown User")
  const [error, setError] = useState(null)
  const [isErrorVisible, setIsErrorVisible] = useState(false)
  const [transcriptStatus, setTranscriptStatus] = useState({ isOngoing: false, message: 'Checking...', status: 'checking' })
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingAction, setProcessingAction] = useState(null)


  const dismissError = () => {
    setIsErrorVisible(false)
    // Delay clearing the error to allow animation to complete
    setTimeout(() => setError(null), 200)
  }

  // Auto-dismiss error after 5 seconds (changed back from 3)
  useEffect(() => {
    if (error && isErrorVisible) {
      const timer = setTimeout(() => {
        dismissError()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, isErrorVisible])

  // Function to check transcript status
  const checkTranscriptStatus = async () => {
    if (!meetingId || !graphToken) {
      setTranscriptStatus({ isOngoing: false, message: 'No meeting context available', status: 'no_meeting' });
      return false; // Return false to indicate polling should stop
    }

    try {
      const response = await fetch("/api/check-transcript-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          meetingId: meetingId, 
          graphToken: graphToken
        }),
      });
      
      const data = await response.json();
      setTranscriptStatus(data);
      
      // Stop polling if no meeting is detected or if there's a persistent error
      if (data.status === 'no_meeting' || data.status === 'meeting_ended') {
        return false; // Return false to indicate polling should stop
      }
      
      return true; // Continue polling
    } catch (error) {
      setTranscriptStatus({ 
        isOngoing: false, 
        message: 'Unable to check transcription status', 
        status: 'error' 
      });
      return true; // Continue polling despite errors (might be temporary)
    }
  };

  // Polling effect for transcript status with intelligent frequency
  useEffect(() => {
    if (meetingId && graphToken) {
      let intervalId;
      let shouldContinuePolling = true;
      
      // Start with immediate check
      checkTranscriptStatus().then(continuePolling => {
        if (!continuePolling) {
          shouldContinuePolling = false;
          return;
        }
        
        // Set up polling interval - much faster for real-time updates
        intervalId = setInterval(async () => {
          if (!shouldContinuePolling) {
            clearInterval(intervalId);
            return;
          }
          
          const continuePolling = await checkTranscriptStatus();
          if (!continuePolling) {
            shouldContinuePolling = false;
            clearInterval(intervalId);
          }
        }, 3000); // Check every 3 seconds for faster updates
      });
      
      // Cleanup on unmount or dependency change
      return () => {
        shouldContinuePolling = false;
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    } else {
      // Set appropriate status when no meeting context
      setTranscriptStatus({ 
        isOngoing: false, 
        message: 'No meeting context available', 
        status: 'no_meeting' 
      });
    }
  }, [meetingId, graphToken]);

  useEffect(() => {
    async function initializeTeamsApp() {
      try {
        await microsoftTeams.app.initialize()
        const context = await microsoftTeams.app.getContext()

        // Handle different contexts for configurable tabs
        let meetingIdValue = null;
        
        // For meeting sidepanel context
        /*if (context.meeting?.id) {
          meetingIdValue = context.meeting.id;
        }
        // For chat context (fallback)
        else */if (context.chat?.id) {
          meetingIdValue = context.chat.id;
        }
        // For team/channel context
        else if (context.channel?.id && context.team?.internalId) {
          meetingIdValue = context.channel.id;
        }
        
        if (meetingIdValue) {
          setMeetingId(meetingIdValue);
        }
        
        // Get user information
        if (context.user?.id) {
          setUserInfo(context.user.id);
        } else if (context.user?.loginHint) {
          setUserInfo(context.user.loginHint);
        } else if (context.user?.userPrincipalName) {
          setUserInfo(context.user.userPrincipalName);
        } else if (context.user?.displayName) {
          setUserInfo(context.user.displayName);
        }
        
        const token = await getSSOToken()

        const response = await fetch("/api/exchangeToken", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            token,
            userId: userInfo || context.user?.id || context.user?.loginHint 
          }),
        })
        const data = await response.json()
        setGraphToken(data.graphToken)
        
        // Register for automatic meeting end polling only if we have a valid meeting ID
        // and we can confirm it's actually a meeting context (not just any chat)
        if (meetingIdValue && data.graphToken) {
          try {
            // First check if this is actually a meeting context before starting polling
            const statusResponse = await fetch("/api/check-transcript-status", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                meetingId: meetingIdValue, 
                graphToken: data.graphToken
              }),
            });
            
            const statusData = await statusResponse.json();
            
            // Only register for automatic polling if we detected a valid meeting
            if (statusData.status !== 'no_meeting') {
              console.log('[CLIENT] Valid meeting detected, registering for automatic polling');
              console.log('[CLIENT] Registration params:', {
                meetingId: meetingIdValue ? `${meetingIdValue.substring(0, 20)}...` : 'missing',
                hasGraphToken: !!data.graphToken,
                userId: userInfo || context.user?.id || 'missing'
              });
              
              const pollingResponse = await fetch("/api/register-meeting-polling", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                  meetingId: meetingIdValue, 
                  graphToken: data.graphToken,
                  userId: userInfo || context.user?.id,
                  agent: "100" // Default agent
                }),
              });
              
              const pollingData = await pollingResponse.json();
              console.log('[CLIENT] Polling registration response:', pollingData);
            } else {
              console.log('[CLIENT] No meeting detected, skipping polling registration. Status:', statusData.status);
            }
          } catch (pollingError) {
            // Don't show error to user as this is background functionality
          }
        }
      } catch (error) {
        // Handle initialization errors silently
      }
    }

    initializeTeamsApp()
  }, [])

  const sendSummarizeRequest = async (action) => {
    // Prevent multiple requests
    if (isProcessing) return;
    
    // Set loading state
    setIsProcessing(true);
    setProcessingAction(action);
    
    try {
      // If the prompt is empty, use a default summary request. Otherwise, use the user's prompt.
      const finalPrompt = prompt.trim() === "" ? "Please provide a summary of the meeting." : prompt

      const payload = {
        agent: selectedAgent,
        prompt: finalPrompt,
        meetingId: meetingId,
        action: action,
        user: userInfo,
        graphToken: graphToken,
      }

      const response = await fetch("/api/summarize", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      // Clear the prompt after sending, regardless of what was sent.
      setPrompt("");
      
      // Show success message temporarily
      if (response.ok) {
        setError(null);
      } else {
        // Show error if response was not ok
        setError(data.message || "An error occurred while processing your request.");
        setIsErrorVisible(true);
      }
      
    } catch (err) {
      // Handle error and show to user
      setError("Failed to send request. Please check your connection and try again.");
      setIsErrorVisible(true);
    } finally {
      // Always reset loading state
      setIsProcessing(false);
      setProcessingAction(null);
    }
  }

  // Aurora Logo Component
  const AuroraLogo = () => (
    <img 
      src="/image.png" 
      alt="Aurora Logo" 
      width="48" 
      height="48" 
      style={{
        display: "block",
        margin: "0 auto"
      }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  )

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      padding: "20px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif"
    }}>
      <style>
        {`
          .glassmorphism-card {
            background: rgba(255, 255, 255, 0.25);
            backdrop-filter: blur(10px);
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.18);
            box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
            padding: 32px;
            margin: 20px auto;
            max-width: 400px;
          }

          .modern-button {
            background: rgba(255, 255, 255, 0.9) !important;
            border: 1px solid rgba(255, 255, 255, 0.3) !important;
            border-radius: 12px !important;
            padding: 12px 24px !important;
            font-weight: 500 !important;
            transition: all 0.3s ease !important;
            backdrop-filter: blur(10px) !important;
            color: #666 !important;
            min-height: 44px !important;
            box-shadow: 0 4px 15px 0 rgba(31, 38, 135, 0.2) !important;
          }

          .modern-button:hover {
            background: rgba(255, 255, 255, 1) !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 25px 0 rgba(31, 38, 135, 0.3) !important;
            color: #333 !important;
          }

          .primary-button {
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%) !important;
            color: white !important;
            border: none !important;
            box-shadow: 0 8px 32px 0 rgba(99, 102, 241, 0.4) !important;
          }

          .primary-button:hover {
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #c026d3 100%) !important;
            color: white !important;
            box-shadow: 0 12px 40px 0 rgba(99, 102, 241, 0.6) !important;
            transform: translateY(-3px) !important;
          }

          .secondary-button {
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%) !important;
            color: white !important;
            border: none !important;
            box-shadow: 0 8px 32px 0 rgba(99, 102, 241, 0.4) !important;
          }

          .secondary-button:hover {
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #c026d3 100%) !important;
            color: white !important;
            box-shadow: 0 12px 40px 0 rgba(99, 102, 241, 0.6) !important;
            transform: translateY(-3px) !important;
          }

          .modern-button:disabled {
            background: rgba(156, 163, 175, 0.5) !important;
            color: rgba(156, 163, 175, 0.8) !important;
            cursor: not-allowed !important;
            transform: none !important;
            box-shadow: 0 2px 8px 0 rgba(156, 163, 175, 0.2) !important;
          }

          .modern-button:disabled:hover {
            background: rgba(156, 163, 175, 0.5) !important;
            color: rgba(156, 163, 175, 0.8) !important;
            transform: none !important;
            box-shadow: 0 2px 8px 0 rgba(156, 163, 175, 0.2) !important;
          }

          .primary-button:disabled,
          .secondary-button:disabled {
            background: rgba(156, 163, 175, 0.5) !important;
            color: rgba(255, 255, 255, 0.6) !important;
            cursor: not-allowed !important;
            transform: none !important;
            box-shadow: 0 2px 8px 0 rgba(156, 163, 175, 0.2) !important;
          }

          .primary-button:disabled:hover,
          .secondary-button:disabled:hover {
            background: rgba(156, 163, 175, 0.5) !important;
            color: rgba(255, 255, 255, 0.6) !important;
            transform: none !important;
            box-shadow: 0 2px 8px 0 rgba(156, 163, 175, 0.2) !important;
          }

          .modern-dropdown {
            background: rgba(255, 255, 255, 0.95) !important;
            border: 2px solid rgba(255, 255, 255, 0.4) !important;
            border-radius: 16px !important;
            backdrop-filter: blur(15px) !important;
            box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 2px 8px rgba(255, 255, 255, 0.3) !important;
            z-index: 1000 !important;
            position: relative !important;
            transition: all 0.3s ease !important;
            min-height: 48px !important;
            padding: 0 16px !important;
          }

          .modern-dropdown:hover {
            background: rgba(255, 255, 255, 0.98) !important;
            border-color: rgba(102, 126, 234, 0.4) !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.2), inset 0 2px 8px rgba(255, 255, 255, 0.4) !important;
          }

          .modern-dropdown .ui-dropdown__container {
            z-index: 1001 !important;
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .modern-dropdown .ui-dropdown__trigger {
            background: transparent !important;
            border: none !important;
            padding: 12px 0 !important;
            font-weight: 500 !important;
            color: #4a5568 !important;
          }

          .modern-dropdown .ui-dropdown__list {
            z-index: 1002 !important;
            background: rgba(255, 255, 255, 0.98) !important;
            backdrop-filter: blur(20px) !important;
            border-radius: 16px !important;
            border: 2px solid rgba(255, 255, 255, 0.4) !important;
            box-shadow: 0 16px 64px 0 rgba(31, 38, 135, 0.25) !important;
            margin-top: 8px !important;
            padding: 8px !important;
          }

          .modern-dropdown .ui-dropdown__item {
            border-radius: 12px !important;
            padding: 12px 16px !important;
            margin: 2px 0 !important;
            transition: all 0.2s ease !important;
            font-weight: 500 !important;
            color: #4a5568 !important;
          }

          .modern-dropdown .ui-dropdown__item:hover {
            background: rgba(99, 102, 241, 0.1) !important;
            color: #6366f1 !important;
            transform: translateX(4px) !important;
          }

          .modern-dropdown .ui-dropdown__item--selected {
            background: rgba(99, 102, 241, 0.15) !important;
            color: #6366f1 !important;
            font-weight: 600 !important;
          }

          .modern-textarea {
            background: rgba(255, 255, 255, 0.95) !important;
            border: 2px solid rgba(255, 255, 255, 0.4) !important;
            border-radius: 20px !important;
            backdrop-filter: blur(15px) !important;
            box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 2px 8px rgba(255, 255, 255, 0.3) !important;
            padding: 20px 24px !important;
            font-size: 14px !important;
            resize: vertical !important;
            min-height: 120px !important;
            transition: all 0.3s ease !important;
            font-family: inherit !important;
            line-height: 1.5 !important;
          }

          .modern-textarea:focus {
            outline: none !important;
            border-color: rgba(102, 126, 234, 0.6) !important;
            box-shadow: 0 8px 40px 0 rgba(102, 126, 234, 0.25), inset 0 2px 8px rgba(255, 255, 255, 0.4) !important;
            background: rgba(255, 255, 255, 0.98) !important;
            transform: translateY(-2px) !important;
          }

          .modern-textarea::placeholder {
            color: #9CA3AF !important;
            font-style: italic !important;
            opacity: 0.8 !important;
          }

          /* Override Fluent UI textarea specific styles */
          .modern-textarea textarea,
          .modern-textarea .ui-input,
          .modern-textarea__wrapper {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
            font-family: inherit !important;
            line-height: 1.5 !important;
            resize: vertical !important;
          }

          .modern-textarea textarea:focus,
          .modern-textarea .ui-input:focus {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }

          .section-title {
            color: #4a5568;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .welcome-text {
            color: #718096;
            font-size: 14px;
            text-align: center;
            margin-top: 8px;
          }

          .error-container {
            overflow: hidden;
            transition: max-height 0.2s ease-out, opacity 0.2s ease-out;
          }

          .error-container.show {
            max-height: 100px;
            opacity: 1;
          }

          .error-container.hide {
            max-height: 0;
            opacity: 0;
          }

          .alert-slide-in {
            animation: slideInDown 0.2s ease-out forwards;
          }

          .alert-slide-out {
            animation: slideOutUp 0.2s ease-in forwards;
          }

          .transcript-status {
            background: rgba(255, 255, 255, 0.9);
            border-radius: 12px;
            padding: 12px 16px;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            transition: all 0.3s ease;
          }

          .transcript-status.active {
            background: rgba(34, 197, 94, 0.1);
            border-color: rgba(34, 197, 94, 0.3);
            color: #059669;
          }

          .transcript-status.inactive {
            background: rgba(239, 68, 68, 0.1);
            border-color: rgba(239, 68, 68, 0.3);
            color: #dc2626;
          }

          .transcript-status.checking {
            background: rgba(59, 130, 246, 0.1);
            border-color: rgba(59, 130, 246, 0.3);
            color: #2563eb;
          }

          .transcript-status.error {
            background: rgba(156, 163, 175, 0.1);
            border-color: rgba(156, 163, 175, 0.3);
            color: #6b7280;
          }

          .status-icon {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            flex-shrink: 0;
          }

          .status-icon.active {
            background: #059669;
            animation: pulse 2s infinite;
          }

          .status-icon.inactive {
            background: #dc2626;
          }

          .status-icon.checking {
            background: #2563eb;
            animation: pulse 1.5s infinite;
          }

          .status-icon.error {
            background: #6b7280;
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }

          @keyframes slideInDown {
            from {
              transform: translateY(-20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          @keyframes slideOutUp {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-20px);
              opacity: 0;
            }
          }

        `}
      </style>
      
      {/* Main Glass Card */}
      <div className="glassmorphism-card">
        {/* Header with Logo */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <AuroraLogo />
          <h1 style={{ 
            margin: "16px 0 8px 0", 
            fontSize: "24px", 
            fontWeight: "600",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            Aurora Notetaker
          </h1>
          <p className="welcome-text">Ask me anything about your meeting!</p>
        </div>

        {/* Transcript Status Indicator */}
        <div className={`transcript-status ${transcriptStatus.status}`}>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            {transcriptStatus.status === 'active' && 'Transcription is ON'}
            {transcriptStatus.status === 'inactive' && 'Please start transcription'}
            {transcriptStatus.status === 'checking' && 'Checking transcription status...'}
            {transcriptStatus.status === 'error' && 'Unable to check transcription'}
            {transcriptStatus.status === 'no_meeting' && 'No meeting detected'}
            {transcriptStatus.status === 'meeting_ended' && 'Meeting has ended'}
          </span>
        </div>

        {/* Error Display */}
        <div className={`error-container ${error && isErrorVisible ? "show" : "hide"}`}>
          {error && (
            <Alert
              danger
              dismissible
              onDismiss={dismissError}
              content={error}
              styles={{ 
                marginBottom: "16px",
                borderRadius: "12px",
                background: "rgba(248, 215, 218, 0.9)",
                backdropFilter: "blur(10px)"
              }}
              className={isErrorVisible ? "alert-slide-in" : "alert-slide-out"}
            />
          )}
        </div>

        {/* Agent Selection */}
        <div style={{ marginBottom: "24px" }}>
          <div className="section-title">Select Agent</div>
          <Dropdown
            placeholder="Choose your AI assistant..."
            items={[
              { key: "100", header: "Default Summarizer" },
              { key: "198", header: "Sales Meeting Summarizer" },
              { key: "199", header: "Tech Analysis Summarizer" },
            ]}
            value={{ value: selectedAgent, header: selectedAgentName }}
            onChange={(e, data) => {
              const agentNameHeader = data.value.header
              const agentKey = data.value.key
              setSelectedAgent(agentKey)
              setSelectedAgentName(agentNameHeader)
            }}
            fluid
            className="modern-dropdown"
            styles={{
              minHeight: "48px",
              borderRadius: "16px"
            }}
          />
        </div>

        {/* Question Input */}
        <div style={{ marginBottom: "24px" }}>
          <div className="section-title">Your Message</div>
          <TextArea
            className="modern-textarea"
            fluid
            placeholder="Type your question here... or click a button below for a quick summary"
            value={prompt}
            onChange={(e, data) => setPrompt(data.value)}
            styles={{
              borderRadius: "20px",
              minHeight: "120px"
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "12px", flexDirection: "column" }}>
          <Button
            fluid
            content={isProcessing && processingAction === "Personal Chat" ? "Processing..." : "Send to Personal Chat"}
            onClick={() => sendSummarizeRequest("Personal Chat")}
            disabled={isProcessing}
            className="modern-button primary-button"
            styles={{ marginBottom: "8px" }}
          />
          <Button
            fluid
            content={isProcessing && processingAction === "Meeting Chat" ? "Processing..." : "Send to Meeting Chat"}
            onClick={() => sendSummarizeRequest("Meeting Chat")}
            disabled={isProcessing}
            className="modern-button secondary-button"
          />
        </div>
      </div>
    </div>
  )
}

export default AuroraSidePanel
